import { RubricStamp } from './../model/rubric-stamp';
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { LanguageBase } from "../model/language";
import { take } from "rxjs/operators";
import { SignatureImageUtils } from "src/app/modules/signature/components/signature-modal-electronic/signature-image.utils";
import { SignatureCanvasUtils } from "src/app/modules/signature/components/signature-modal-electronic/signature-canvas-utils";
import { SignatoryStamp } from "../model/signatory-stamp";
import { FileUtil } from "src/app/util/file-utils";
import { InteractService } from "./interact.service";
import { Signatory } from "../model/signatory";
import { v4 as uuidv4 } from 'uuid';
import { BrowserUtil } from 'src/app/util/browser-util';
import { Subject } from "rxjs";
import { StampsValidatorService } from './stampsValidator.service';
import { FormCustomField } from '../model/form-custom-field';
import { CustomFieldTypeEnum } from 'src/app/enum/custom-field-type.enum';
import { HTMLElementUtils } from 'src/app/util/html-element-utils';

interface CustomFieldResponsible {
  id: number;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class ComponentDragDropService {
  private readonly textFieldLineHeight = 1.2;

  onDelete = new Subject<string[]>();
  onDeleteRubric = new Subject<string[]>();
  onDeleteCustomField = new Subject<string[]>();
  onEditCustomField = new Subject<FormCustomField>();
  isDragging = new Subject<boolean>();
  languageBase: LanguageBase;
  backgroundImage = SignatureImageUtils.backgroundImage;
  isMobile: boolean = BrowserUtil.isMobile();

  constructor(
    private signatureCanvasUtils: SignatureCanvasUtils,
    private interactService: InteractService,
    private translate: TranslateService,
    private stampsvalidatorService: StampsValidatorService) {
    this.languageBase = new LanguageBase(translate);
    this.languageBase.init().pipe(take(1)).subscribe();
    
    document.addEventListener('pointerup', () => {
      this.isDragging.next(false);
    });

    document.addEventListener('pointercancel', () => {
      this.isDragging.next(false);
    });
  }

  createElementSignatureAddStamp(nameEnvelopeFile: string, signatory: Signatory, page: number, positionPixelX: number, positionPixelY: number, adjustmentMarginY: number = 0, loadSignature: boolean = false, bCreateHtml = true) {
    const MARGIN_X_LEFT_ON_VIEWER = 13.45;

    const textCanvas = this.signatureCanvasUtils.newCanvasSignatory({ canvasId: '', });
    this.signatureCanvasUtils
      .addBackgroundSignatoryToCanvas(textCanvas, { image: this.backgroundImage })
      .then(() => {
        var img = <HTMLImageElement>(document.createElement('img'));
        img.classList.add('signature-image');
        img.setAttribute('style', 'background-color: rgba(255, 255, 255, 0.5); pointer-events: auto; border: 1px dashed green; width: 100%; height: 100%;');

        this.initCanvas(signatory, textCanvas);
        img.src = this.signatureCanvasUtils.exportCanvasAsImage(textCanvas, { format: 'png' });

        const adjustmentY = positionPixelY - (adjustmentMarginY > 0 ? adjustmentMarginY : 12)
        const coordenateMillimeterX = Math.round((positionPixelX / 3.78 + Number.EPSILON) * 100) / 100;
        const coordenateMillimeterY = Math.round(((adjustmentMarginY < 0 ? adjustmentY : positionPixelY) / 3.78 + Number.EPSILON) * 100) / 100;

        const stamp = new SignatoryStamp();
        const coordenateMillimeterWidth = Math.round((stamp.signatureSizePixelWidth / 3.78 + Number.EPSILON) * 100) / 100;
        const coordenateMillimeterHeight = Math.round((stamp.signatureSizePixelHeight / 3.78 + Number.EPSILON) * 100) / 100;

        stamp.gId = uuidv4();
        stamp.fileName = FileUtil.removeExt(nameEnvelopeFile);
        stamp.invalid = false;
        stamp.signatoryId = signatory.id.toString();
        stamp.signaturePositionX = coordenateMillimeterX;
        stamp.signaturePositionY = coordenateMillimeterY;
        stamp.signaturePositionPixelX = (positionPixelX + MARGIN_X_LEFT_ON_VIEWER);
        stamp.signaturePositionPixelY = adjustmentY;
        stamp.signatureSizeWidth = coordenateMillimeterWidth;
        stamp.signatureSizeHeight = coordenateMillimeterHeight;
        stamp.default = loadSignature;
        stamp.signaturePositionPage = page.toString();
        this.interactService.addSignatoryStamp = stamp;

        if (bCreateHtml) {
          const element = <HTMLDivElement>document.createElement("div");
          element.setAttribute('data-gId', stamp.gId.toString());
          element.setAttribute('data-id', stamp.signatoryId);
          element.setAttribute('ngbTooltip', signatory.name);
          element.setAttribute('out-document', 'false');
          element.setAttribute("id", FileUtil.removeExt(nameEnvelopeFile));
          element.setAttribute('data-type', "signature");

          let trashButton = this.createButtonDeleteSignature(stamp.gId.toString(), nameEnvelopeFile);
          let resizeIcon = this.createResizeIcon(stamp.gId.toString(), nameEnvelopeFile);

          let positionX = 0;
          let positionY = 0;

          element.classList.add('signature');
          if (loadSignature) {
            element.classList.add('signature-default');
            positionX = stamp.signaturePositionPixelX;
            positionY =  stamp.signaturePositionPixelY;
          } else{
            positionX = stamp.signaturePositionPixelX;
            positionY = stamp.signaturePositionPixelY + 20;
          }

          element.setAttribute('data-x', positionX.toString());
          element.setAttribute('data-y', positionY.toString());

          element.setAttribute('style',
            'position: absolute; z-index: 9999; box-sizing: border-box; touch-action: none; ' +
            'transform: translate(' + positionX.toString() + 'px, ' + positionY.toString() + 'px); ' +
            'width:' + stamp.signatureSizePixelWidth.toString() + 'px; height:' + stamp.signatureSizePixelHeight.toString() + 'px;' +
            'user-select: none;');

          element.appendChild(img);
          if (!this.isMobile) {
            element.appendChild(trashButton);
            element.appendChild(resizeIcon);
          }

          setTimeout(() => {
            const currentDiv = document.getElementsByClassName('page dropzone')[page - 1];
            if (currentDiv) currentDiv.insertBefore(element, currentDiv.firstChild)
            this.addDragListener(element);
            this.stampsvalidatorService.onAfterCheckStamps();
          }, 300);
        }
      });
  }

  createElementSignature(signatoryStamp: SignatoryStamp, signatory: Signatory, loadSignature: boolean = false) {
    const textCanvas = this.signatureCanvasUtils.newCanvasSignatory({ canvasId: '', });
    this.signatureCanvasUtils
      .addBackgroundSignatoryToCanvas(textCanvas, { image: this.backgroundImage })
      .then(() => {

        var img = <HTMLImageElement>(document.createElement('img'));
        img.classList.add('signature-image');
        img.setAttribute('style', 'background-color: rgba(255, 255, 255, 0.5); pointer-events: auto; border: 1px dashed green; width: 100%; height: 100%;');

        this.initCanvas(signatory, textCanvas);
        img.src = this.signatureCanvasUtils.exportCanvasAsImage(textCanvas, { format: 'png' });

        const element = <HTMLDivElement>document.createElement("div");

        element.setAttribute('data-gId', signatoryStamp.gId.toString());
        element.setAttribute('data-id', signatoryStamp.signatoryId);
        element.setAttribute('ngbTooltip', signatory.name);
        element.setAttribute('out-document', 'false');
        element.setAttribute("id", FileUtil.removeExt(signatoryStamp.fileName));
        element.setAttribute('data-type', "signature");
        element.setAttribute('style', 'box-sizing: border-box');

        let trashButton = this.createButtonDeleteSignature(signatoryStamp.gId.toString(), signatoryStamp.fileName);
        let resizeIcon = this.createResizeIcon(signatoryStamp.gId.toString(), signatoryStamp.fileName);

        let positionX = 0;
        let positionY = 0;

        element.classList.add('signature');
        if (loadSignature) {
          element.classList.add('signature-default');
          positionX = signatoryStamp.signaturePositionPixelX;
          positionY =  signatoryStamp.signaturePositionPixelY;
        } else{
          positionX = signatoryStamp.signaturePositionPixelX;
          positionY = signatoryStamp.signaturePositionPixelY + 20;
        }

        element.setAttribute('data-x', positionX.toString());
        element.setAttribute('data-y', positionY.toString());

        element.setAttribute('style',
          'position: absolute; z-index: 9999; box-sizing: border-box; touch-action: none; ' +
          'transform: translate(' + positionX.toString() + 'px, ' + positionY.toString() + 'px); ' +
          'width:' + signatoryStamp.signatureSizePixelWidth.toString() + 'px; height:' + signatoryStamp.signatureSizePixelHeight.toString() + 'px;' +
          'user-select: none;');
        element.appendChild(img);
        if (!this.isMobile) {
          element.appendChild(trashButton);
          element.appendChild(resizeIcon);
        }
        
        setTimeout(() => {
          const currentDiv = document.getElementsByClassName('page dropzone')[parseInt(signatoryStamp.signaturePositionPage) - 1];
          if (currentDiv) currentDiv.insertBefore(element, currentDiv.firstChild);
          this.addDragListener(element);
          this.stampsvalidatorService.onAfterCheckStamps();
        }, 300);
      });
  }

  createElementRubricAddStamp(nameEnvelopeFile: string, signatory: Signatory, page: number, positionPixelX: number, positionPixelY: number, loadRubric: boolean = false, bCreateHtml = true) {
    const textCanvas = this.signatureCanvasUtils.newCanvasRubric({ canvasId: '', });
    this.signatureCanvasUtils
      .addBackgroundSignatoryToCanvas(textCanvas, { image: this.backgroundImage })
      .then(() => {

        var img = <HTMLImageElement>(document.createElement('img'));
        img.classList.add('rubric-image');
        img.setAttribute('style', 'background-color: rgba(255, 255, 255, 0.5); pointer-events: auto; border: 1px dashed green; width: 100%; height: 100%;');

        this.initRubricCanvas(signatory, textCanvas);
        img.src = this.signatureCanvasUtils.exportCanvasAsImage(textCanvas, { format: 'png' });

        const coordenateMillimeterX = Math.round((positionPixelX / 3.78 + Number.EPSILON) * 100) / 100;
        const coordenateMillimeterY = Math.round((positionPixelY / 3.78 + Number.EPSILON) * 100) / 100;

        const rubric = new RubricStamp();

        const coordenateMillimeterWidth = Math.round((rubric.sizePixelWidth / 3.78 + Number.EPSILON) * 100) / 100;
        const coordenateMillimeterHeight = Math.round((rubric.sizePixelHeight / 3.78 + Number.EPSILON) * 100) / 100;

        rubric.gId = uuidv4();
        rubric.fileName = FileUtil.removeExt(nameEnvelopeFile);
        rubric.invalid = false;
        rubric.signatoryId = signatory.id.toString();
        rubric.positionX = coordenateMillimeterX;
        rubric.positionY = coordenateMillimeterY;
        rubric.positionPixelX = positionPixelX;
        rubric.positionPixelY = positionPixelY;
        rubric.sizeWidth = coordenateMillimeterWidth;
        rubric.sizeHeight = coordenateMillimeterHeight;
        rubric.default = loadRubric;
        rubric.page = page.toString();
        this.interactService.addRubricStamp = rubric;

        if (bCreateHtml) {

          const element = <HTMLDivElement>document.createElement("div");

          element.setAttribute('data-gId', rubric.gId.toString());
          element.setAttribute('data-id', rubric.signatoryId);
          element.setAttribute('ngbTooltip', signatory.name)
          element.setAttribute('out-document', 'false');
          element.setAttribute("id", FileUtil.removeExt(nameEnvelopeFile));
          element.setAttribute('data-type', "rubric");
          element.setAttribute('style', 'box-sizing: border-box');

          let trashButton = this.createButtonDeleteRubric(rubric.gId.toString(), nameEnvelopeFile);
          let resizeIcon = this.createResizeIcon(rubric.gId.toString(), nameEnvelopeFile);

          let positionX = 0;
          let positionY = 0;

          element.classList.add('rubric');
          if (loadRubric) {
            element.classList.add('rubric-default');
            positionX = rubric.positionPixelX - 5;
            positionY = rubric.positionPixelY + 10;
          } else{
            positionX = rubric.positionPixelX + 10;
            positionY = rubric.positionPixelY + 10;
          }

          element.setAttribute('data-x', positionX.toString());
          element.setAttribute('data-y', positionY.toString());

          element.setAttribute('style',
            'position: absolute; z-index: 9999; box-sizing: border-box; touch-action: none; ' +
            'transform: translate(' + positionX.toString() + 'px, ' + positionY.toString() + 'px); ' +
            'width:' + rubric.sizePixelWidth.toString() + 'px; height:' + rubric.sizePixelHeight.toString() + 'px;' +
            'user-select: none;');

          element.appendChild(img);
          if (!this.isMobile) {
            element.appendChild(trashButton);
            element.appendChild(resizeIcon);
          }

          setTimeout(() => {
            const currentDiv = document.getElementsByClassName('page dropzone')[page - 1];
            if (currentDiv) currentDiv.insertBefore(element, currentDiv.firstChild)
            this.addDragListener(element);
            this.stampsvalidatorService.onAfterCheckStamps();
          }, 300);
        }
      });
  }

  createElementRubric(rubricStamp: RubricStamp, signatory: Signatory, loadRubric: boolean = false) {
    const textCanvas = this.signatureCanvasUtils.newCanvasRubric({ canvasId: '', });
    this.signatureCanvasUtils
      .addBackgroundSignatoryToCanvas(textCanvas, { image: this.backgroundImage })
      .then(() => {

        var img = <HTMLImageElement>(document.createElement('img'));
        img.classList.add('rubric-image');
        img.setAttribute('style', 'background-color: rgba(255, 255, 255, 0.5); pointer-events: auto; border: 1px dashed green; width: 100%; height: 100%;');

        this.initRubricCanvas(signatory, textCanvas);
        img.src = this.signatureCanvasUtils.exportCanvasAsImage(textCanvas, { format: 'png' });

        const element = <HTMLDivElement>document.createElement("div");

        element.setAttribute('data-gId', rubricStamp.gId.toString());
        element.setAttribute('data-id', rubricStamp.signatoryId);
        element.setAttribute('ngbTooltip', signatory.name);
        element.setAttribute('out-document', 'false');
        element.setAttribute("id", FileUtil.removeExt(rubricStamp.fileName));
        element.setAttribute('data-type', "rubric");
        element.setAttribute('style', 'box-sizing: border-box');

        let trashButton = this.createButtonDeleteRubric(rubricStamp.gId.toString(), rubricStamp.fileName);
        let resizeIcon = this.createResizeIcon(rubricStamp.gId.toString(), rubricStamp.fileName);

        let positionX = 0;
        let positionY = 0;

        element.classList.add('rubric');
        if (loadRubric) {
          element.classList.add('rubric-default');
          positionX = rubricStamp.positionPixelX - 5;
          positionY = rubricStamp.positionPixelY + 20;
        } else{
          positionX = rubricStamp.positionPixelX + 10;
          positionY = rubricStamp.positionPixelY + 10;
        }

        element.setAttribute('data-x', positionX.toString());
        element.setAttribute('data-y', positionY.toString());

        element.setAttribute('style',
          'position: absolute; z-index: 9999; box-sizing: border-box; touch-action: none; ' +
          'transform: translate(' + positionX.toString() + 'px, ' + positionY.toString() + 'px); ' +
          'width:' + rubricStamp.sizePixelWidth.toString() + 'px; height:' + rubricStamp.sizePixelHeight.toString() + 'px;' +
          'user-select: none;');

        element.appendChild(img);
        if (!this.isMobile) {
          element.appendChild(trashButton);
          element.appendChild(resizeIcon);
        }

        setTimeout(() => {
          const currentDiv = document.getElementsByClassName('page dropzone')[parseInt(rubricStamp.page) - 1];
          if (currentDiv) currentDiv.insertBefore(element, currentDiv.firstChild)
          this.addDragListener(element);
          this.stampsvalidatorService.onAfterCheckStamps();
        }, 300);
      });
  }

  createElementCustomFieldAdd(
    nameEnvelopeFile: string,
    responsible: CustomFieldResponsible,
    page: number,
    positionPixelX: number,
    positionPixelY: number,
    customFieldType: CustomFieldTypeEnum,
    bCreateHtml = true
  ) {
    if (!responsible) {
      return;
    }

    const label = document.createElement('label') as HTMLLabelElement;
    const gId = uuidv4();
    label.classList.add(`${customFieldType.toString()}-image`);
    label.setAttribute('data-gId', gId.toString());
    label.setAttribute('style', this.getCustomFieldLabelStyle());

    if (customFieldType !== CustomFieldTypeEnum.CheckboxField) {
      label.textContent = '';
    }

    const coordenateMillimeterX = Math.round((positionPixelX / 3.78 + Number.EPSILON) * 100) / 100;
    const coordenateMillimeterY = Math.round((positionPixelY / 3.78 + Number.EPSILON) * 100) / 100;

    const customField = new FormCustomField();
    customField.gId = gId;
    customField.fileName = FileUtil.removeExt(nameEnvelopeFile);
    customField.invalid = false;
    customField.signatoryId = responsible.id.toString();
    customField.positionX = coordenateMillimeterX;
    customField.positionY = coordenateMillimeterY;
    customField.positionPixelX = positionPixelX;
    customField.positionPixelY = positionPixelY;
    customField.page = page.toString();
    customField.fieldType = customFieldType;
    customField.displayName = label.textContent.replace('*', '').trim();
    customField.fontSize = 12;
    customField.required = false;
    customField.isNew = false;
    this.setCustomFieldSizeDefault(customField);

    this.interactService.addCustomField = customField;

    if (bCreateHtml) {
      this.createCustomFieldElement(customField, responsible, label, page);
      const eventField = new FormCustomField();
      Object.assign(eventField, customField);
      eventField.isNew = true;
      this.onEditCustomField.next(eventField);
    }
  }

  createElementCustomField(customField: FormCustomField, responsible: CustomFieldResponsible) {
    if (!responsible) {
      return;
    }

    setTimeout(() => {
      const label = document.createElement('label') as HTMLLabelElement;
      label.classList.add('signature-image');
      label.setAttribute('style', this.getCustomFieldLabelStyle());

      const defaultLabel = '';

      if (customField.fieldType !== CustomFieldTypeEnum.CheckboxField) {
        label.textContent = customField.displayName || defaultLabel;
      }

      this.createCustomFieldElement(customField, responsible, label, parseInt(customField.page));
    }, 100);
  }

  private addDragListener(element: HTMLElement) {
    element.addEventListener('pointerdown', () => {
      this.isDragging.next(true);
    });
  }

  private initCanvas(el: Signatory, textCanvas) {
    this.signatureCanvasUtils.addTextTitleSignatory(textCanvas, this.translate.instant('signatureModal.headerText'), 2);
    const signatoryTypeRole = el.signatoryTypeRole.isDefault ? this.translate.instant(el.signatoryTypeRole.name) : el.signatoryTypeRole.name;
    this.signatureCanvasUtils.addTextTitleSignatory(textCanvas, signatoryTypeRole, 20);
    this.signatureCanvasUtils.addTextSignatory(textCanvas, el.name);
    this.signatureCanvasUtils.addTextFullNameSignatory(textCanvas, el.name);
  }

  initRubricCanvas(el: Signatory, textCanvas) {
    const name = el.name.trim().split(' ');
    const first = [...name].shift();
    const last = [...name].pop();
    this.signatureCanvasUtils.addTextTitleRubric(textCanvas, this.translate.instant('signatureModal.rubric'));
    this.signatureCanvasUtils.addTextRubric(textCanvas, first[0].toUpperCase() + last[0].toUpperCase());
  }

  private createButtonDeleteSignature(gId: string, fileName: string) {
    const trashButton = document.createElement('button');
    trashButton.setAttribute('style', 'position: absolute; right: 3px;top: 3px; border: none;')
    const trashIcon = document.createElement('i');
    trashIcon.classList.add('far');
    trashIcon.classList.add('fa-trash-alt');
    trashIcon.setAttribute('data-gId', gId);
    trashIcon.setAttribute("fileName", FileUtil.removeExt(fileName));
    trashButton.addEventListener('click', (ev) => {
      this.onDelete.next([gId, FileUtil.removeExt(fileName)]);
    });

    trashButton.append(trashIcon);
    return trashButton;
  }

  private createButtonDeleteRubric(gId: string, fileName: string) {
    const trashButton = document.createElement('button');
    trashButton.setAttribute('style', 'position: absolute; right:3px; top:3px; border:none;')
    const trashIcon = document.createElement('i');
    trashIcon.classList.add('far');
    trashIcon.classList.add('fa-trash-alt');
    trashIcon.setAttribute('data-gId', gId);
    trashIcon.setAttribute("fileName", FileUtil.removeExt(fileName));
    trashButton.addEventListener('click', (ev) => {
      this.onDeleteRubric.next([gId, FileUtil.removeExt(fileName)]);
    });

    trashButton.append(trashIcon);
    return trashButton;
  }

  private createResizeIcon(gId: string, fileName: string) {
    const resizeIcon = document.createElement('i');
    resizeIcon.setAttribute('style', 'position: absolute; right:3px; bottom: 3px; border:none;')
    resizeIcon.classList.add('far');
    resizeIcon.classList.add('fa-expand-arrows');
    resizeIcon.setAttribute('data-gId', gId);
    resizeIcon.setAttribute("fileName", FileUtil.removeExt(fileName));
    return resizeIcon;
  }

  private createCustomFieldElement(
    customField: FormCustomField,
    responsible: CustomFieldResponsible,
    label: HTMLLabelElement,
    page: number
  ) {
    const element = document.createElement('div') as HTMLDivElement;
    element.setAttribute('data-gId', customField.gId.toString());
    element.setAttribute('data-page', customField.page.toString());
    element.setAttribute('data-id', customField.signatoryId);
    element.setAttribute('ngbTooltip', responsible?.name ?? '');
    element.setAttribute('data-x', customField.positionPixelX.toString());
    element.setAttribute('data-y', customField.positionPixelY.toString());
    element.setAttribute('out-document', 'false');
    element.setAttribute('id', FileUtil.removeExt(customField.fileName));
    element.setAttribute('data-type', customField.fieldType.toString());
    element.classList.add(customField.fieldType.toString());
    element.setAttribute('style', this.styleCustomField(customField));

    if (customField.fieldType === CustomFieldTypeEnum.CheckboxField) {
      const checkboxIcon = this.createCheckboxIcon(customField);
      element.appendChild(checkboxIcon);
    }

    element.addEventListener('dblclick', () => {
      const fieldToEdit = new FormCustomField();
      Object.assign(fieldToEdit, customField);
      this.onEditCustomField.next(fieldToEdit);
    });

    if (customField.fieldType === CustomFieldTypeEnum.CheckboxField) {
      label.style.height = '0px';
      label.style.maxHeight = '0px';
      label.style.width = '0px';
      label.style.maxWidth = '0px';
    }

    element.appendChild(label);

    if (customField.fieldType !== CustomFieldTypeEnum.CheckboxField) {
      if (customField.required) {
        element.appendChild(HTMLElementUtils.createSpanRequired(false));
      }
    } else if (customField.required) {
      element.appendChild(HTMLElementUtils.createSpanRequired(true));
    }

    const currentDiv = document.getElementsByClassName('page dropzone')[page - 1];
    currentDiv.insertBefore(element, currentDiv.firstChild);
    this.addDragListener(element);
    this.stampsvalidatorService.onAfterCheckStamps();
  }

  updateCustomField(customField: FormCustomField) {
    const currentCustomField = this.interactService.customFields.find((x) => x.gId === customField.gId);
    if (!currentCustomField) {
      return;
    }

    currentCustomField.displayName = customField.displayName;
    currentCustomField.required = customField.required;
    currentCustomField.fontSize = customField.fontSize;
    currentCustomField.numberOfLines = customField.numberOfLines;
    currentCustomField.isNew = false;
    currentCustomField.sizeWidth = customField.sizeWidth;
    currentCustomField.sizeHeight = customField.sizeHeight;
    currentCustomField.sizePixelWidth = customField.sizePixelWidth;
    currentCustomField.sizePixelHeight = customField.sizePixelHeight;

    const element = document.querySelector(
      `.${currentCustomField.fieldType}[data-gid="${currentCustomField.gId}"]`
    ) as HTMLElement;

    if (!element) {
      return;
    }

    const label = element.querySelector('label') as HTMLLabelElement;
    if (!label) {
      return;
    }

    label.textContent = '';
    label.style.fontSize = `${currentCustomField.fontSize || 12}px`;
    label.style.lineHeight = `${this.textFieldLineHeight}`;
    label.style.display = 'block';
    label.style.margin = '0';
    label.style.padding = '0';
    label.style.boxSizing = 'border-box';
    label.style.width = '100%';
    label.style.height = '100%';
    label.style.overflow = 'hidden';
    label.style.whiteSpace = 'pre-wrap';
    label.style.wordBreak = 'break-word';
    if (currentCustomField.fieldType !== CustomFieldTypeEnum.CheckboxField) label.textContent = (currentCustomField.displayName || '').trim();

    if (currentCustomField.fieldType === CustomFieldTypeEnum.CheckboxField) {
      element.style.width = `${customField.sizePixelWidth}px`;
      element.style.height = `${customField.sizePixelHeight}px`;
    } else {
      element.style.height = `${customField.sizePixelHeight}px`;
    }

    const existingAsterisk = element.querySelector('.required-asterisk');

    if (currentCustomField.required) {
      if (!existingAsterisk) {
        element.appendChild(HTMLElementUtils.createSpanRequired(currentCustomField.fieldType === CustomFieldTypeEnum.CheckboxField));
      }
    } else {
      if (existingAsterisk) {
        existingAsterisk.remove();
      }
    }
  }

  private createButtonDeleteCustomField(gId: string, fileName: string) {
    const trashButton = document.createElement('button');
    trashButton.setAttribute('style', 'position: absolute; right:3px; top:3px; border:none;');
    const trashIcon = document.createElement('i');
    trashIcon.classList.add('far');
    trashIcon.classList.add('fa-trash-alt');
    trashIcon.setAttribute('data-gId', gId);
    trashIcon.setAttribute('fileName', FileUtil.removeExt(fileName));
    trashButton.addEventListener('click', () => {
      this.onDeleteCustomField.next([gId, FileUtil.removeExt(fileName)]);
    });

    trashButton.append(trashIcon);
    return trashButton;
  }

  private createCheckboxIcon(customField: FormCustomField): HTMLElement {
    const checkboxIcon = document.createElement('i');

    checkboxIcon.style.width = `100%`;
    checkboxIcon.style.height = `100%`;
    checkboxIcon.style.display = 'flex';
    checkboxIcon.style.alignItems = 'center';
    checkboxIcon.style.justifyContent = 'center';

    checkboxIcon.classList.add('far');
    checkboxIcon.classList.add('fa-square');

    checkboxIcon.setAttribute('data-gId', customField.gId.toString());
    checkboxIcon.setAttribute('fileName', FileUtil.removeExt(customField.fileName));

    return checkboxIcon;
  }

  private setCustomFieldSizeDefault(customField: FormCustomField) {
    let width = 250;
    let height = 25;

    if (customField.fieldType === CustomFieldTypeEnum.CheckboxField) {
      width = 20;
      height = 20;
    }

    const sizeHeight = Math.round((height / 3.78 + Number.EPSILON) * 100) / 100;
    const sizeWidth = Math.round((width / 3.78 + Number.EPSILON) * 100) / 100;

    customField.sizeHeight = sizeHeight;
    customField.sizePixelHeight = height;
    customField.sizeWidth = sizeWidth;
    customField.sizePixelWidth = width;
  }

  private styleCustomField(customField: FormCustomField) {
    if (customField.fieldType === CustomFieldTypeEnum.CheckboxField) {
      return `position: absolute;
      z-index: 9999;
      width: ${customField.sizePixelWidth}px;
      height: ${customField.sizePixelHeight}px;
      box-sizing: border-box;
      touch-action: none;
      display: flex;
      justify-content: center;
      align-items: center;
      transform: translate(${customField.positionPixelX.toString()}px, ${customField.positionPixelY.toString()}px);
      padding: 3px
      user-select: none;`;
    } else {
      return `position: absolute;
      z-index: 9999;
      width: ${customField.sizePixelWidth}px;
      height: ${customField.sizePixelHeight}px;
      box-sizing: border-box;
      touch-action: none;
      transform: translate(${customField.positionPixelX.toString()}px, ${customField.positionPixelY.toString()}px); padding: 2px
      user-select: none;`;
    }
  }

  private getCustomFieldLabelStyle(): string {
    return `background-color: rgba(255, 255, 255, 0.5);
    pointer-events: auto;
    cursor: move;
    user-select: none;
    display: block;
    width: 100%;
    height: 100%;
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    line-height: ${this.textFieldLineHeight};
    overflow: hidden;
    white-space: pre-wrap;
    word-break: break-word;`;
  }
}
